// shared/api/journal.js
// Journal API - All journal-related functions
import { supabase } from '../supabase';

/**
 * Save a journal entry
 * @param {Object} entryData - {user_id, session_id, content, word_count, technique_id}
 * @returns {Promise<{entry, error}>}
 */
export const saveJournalEntry = async (entryData) => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .insert({
        ...entryData,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    console.log('✅ Journal entry saved:', data.id);
    return { entry: data, error: null };
  } catch (error) {
    console.error('Error saving journal entry:', error);
    return { entry: null, error };
  }
};

/**
 * Fetch user's journal entries
 * @param {string} userId 
 * @param {Object} options - {limit, sessionId}
 * @returns {Promise<{entries, error}>}
 */
export const fetchJournalEntries = async (userId, options = {}) => {
  try {
    let query = supabase
      .from('journal_entries')
      .select(`
        *,
        daily_sessions (
          id,
          journey_id,
          created_at,
          journeys (
            id,
            title,
            category
          )
        )
      `)
      .eq('user_id', userId);

    // Filter by session if provided
    if (options.sessionId) {
      query = query.eq('session_id', options.sessionId);
    }

    // Apply ordering
    query = query.order('created_at', { ascending: false });

    // Apply limit
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error} = await query;

    if (error) throw error;
    return { entries: data || [], error: null };
  } catch (error) {
    console.error('Error fetching journal entries:', error);
    return { entries: [], error };
  }
};

/**
 * Get single journal entry by ID
 * @param {string} entryId 
 * @returns {Promise<{entry, error}>}
 */
export const getJournalEntryById = async (entryId) => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .select(`
        *,
        daily_sessions (
          id,
          journey_id,
          journeys (
            id,
            title,
            category
          )
        )
      `)
      .eq('id', entryId)
      .single();

    if (error) throw error;
    return { entry: data, error: null };
  } catch (error) {
    console.error('Error fetching journal entry:', error);
    return { entry: null, error };
  }
};

/**
 * Update journal entry
 * @param {string} entryId 
 * @param {Object} updates - {content, word_count, etc}
 * @returns {Promise<{entry, error}>}
 */
export const updateJournalEntry = async (entryId, updates) => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', entryId)
      .select()
      .single();

    if (error) throw error;
    return { entry: data, error: null };
  } catch (error) {
    console.error('Error updating journal entry:', error);
    return { entry: null, error };
  }
};

/**
 * Delete journal entry
 * @param {string} entryId 
 * @returns {Promise<{success, error}>}
 */
export const deleteJournalEntry = async (entryId) => {
  try {
    const { error } = await supabase
      .from('journal_entries')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
    console.log('✅ Journal entry deleted:', entryId);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting journal entry:', error);
    return { success: false, error };
  }
};

/**
 * Fetch journal entries for a specific journey
 * @param {string} userId 
 * @param {string} journeyId 
 * @returns {Promise<{entries, error}>}
 */
export const fetchJournalEntriesByJourney = async (userId, journeyId) => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .select(`
        *,
        daily_sessions!inner (
          id,
          journey_id,
          created_at,
          journeys (
            id,
            title,
            category
          )
        )
      `)
      .eq('user_id', userId)
      .eq('daily_sessions.journey_id', journeyId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { entries: data || [], error: null };
  } catch (error) {
    console.error('Error fetching journal entries by journey:', error);
    return { entries: [], error };
  }
};

/**
 * Get journal statistics for user
 * @param {string} userId 
 * @returns {Promise<{stats, error}>}
 */
export const getJournalStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .select('id, word_count, created_at')
      .eq('user_id', userId);

    if (error) throw error;

    const stats = {
      totalEntries: data.length,
      totalWords: data.reduce((sum, entry) => sum + (entry.word_count || 0), 0),
      averageWords: data.length > 0 
        ? Math.round(data.reduce((sum, entry) => sum + (entry.word_count || 0), 0) / data.length)
        : 0,
      entriesThisMonth: data.filter(entry => {
        const entryDate = new Date(entry.created_at);
        const now = new Date();
        return entryDate.getMonth() === now.getMonth() && 
               entryDate.getFullYear() === now.getFullYear();
      }).length
    };

    return { stats, error: null };
  } catch (error) {
    console.error('Error fetching journal stats:', error);
    return { stats: null, error };
  }
};

/**
 * Search journal entries by content
 * @param {string} userId 
 * @param {string} searchTerm 
 * @returns {Promise<{entries, error}>}
 */
export const searchJournalEntries = async (userId, searchTerm) => {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .select(`
        *,
        daily_sessions (
          id,
          journey_id,
          journeys (
            id,
            title,
            category
          )
        )
      `)
      .eq('user_id', userId)
      .ilike('content', `%${searchTerm}%`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { entries: data || [], error: null };
  } catch (error) {
    console.error('Error searching journal entries:', error);
    return { entries: [], error };
  }
};
